import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import api from "../services/api";
import { Property } from "../global/types";

export interface UseCityPropertiesOptions {
  city: string | null;
  uf: string | null;
  apiParams: Record<string, unknown>;
  enabled?: boolean;
}

export function useCityProperties({
  city,
  uf,
  apiParams,
  enabled = true,
}: UseCityPropertiesOptions) {
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(() => enabled && !!city);
  const [error, setError] = useState<string | null>(null);
  const [activeNeighborhoodFilter, setActiveNeighborhoodFilter] = useState<
    string | null
  >(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeRequestIdRef = useRef(0);
  const apiParamsKey = JSON.stringify(apiParams);

  useLayoutEffect(() => {
    if (enabled && city) {
      setIsLoading(true);
      setError(null);
    } else {
      setAllProperties([]);
      setIsLoading(false);
    }
  }, [city, uf, apiParamsKey, enabled]);

  const fetchAllProperties = useCallback(async () => {
    if (!enabled || !city) {
      setAllProperties([]);
      setIsLoading(false);
      return [];
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const requestId = ++activeRequestIdRef.current;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    const requestParams = {
      ...apiParams,
      city,
      ...(uf ? { uf } : {}),
    };

    try {
      const { data } = await api.get("/api/client/properties", {
        params: requestParams,
        signal: abortController.signal,
      });

      if (requestId !== activeRequestIdRef.current) {
        return [];
      }

      const properties: Property[] = data?.data ?? [];
      setAllProperties(properties);

      return properties;
    } catch (err: unknown) {
      if (requestId !== activeRequestIdRef.current) {
        return [];
      }

      const isAbort =
        err instanceof Error &&
        (err.name === "AbortError" || err.name === "CanceledError");

      if (!isAbort) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
      }

      return [];
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [apiParamsKey, city, enabled, uf]);

  const applyNeighborhoodFilter = useCallback(
    (neighborhood: string | null) => {
      setActiveNeighborhoodFilter(neighborhood);
    },
    []
  );

  const filteredProperties =
    activeNeighborhoodFilter === null
      ? allProperties
      : allProperties.filter(
          (property) =>
            property.address?.neighborhood?.toLowerCase() ===
            activeNeighborhoodFilter.toLowerCase()
        );

  useEffect(() => {
    fetchAllProperties();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchAllProperties]);

  return {
    allProperties,
    filteredProperties,
    isLoading,
    error,
    activeNeighborhoodFilter,
    applyNeighborhoodFilter,
    refetch: fetchAllProperties,
  };
}
